import { formatCurrency } from '../receptionistData';
import {
  parsePaymentSplitsFromRow,
  resolveSplitPayment,
  sumPaymentSplits,
} from '../../../utils/splitPaymentUtils';
import {
  isWalkInGymMemberId,
  isWalkInMemberName,
} from '../../../services/receptionist/walkInMember';
import { isValidIsoDate } from './saleDurationUtils';

function receiptDate(value) {
  return isValidIsoDate(value) ? String(value).slice(0, 10) : '';
}

const SETTLED_STATUSES = new Set([
  'complimentary',
  'completed',
  'paid',
  'cancelled',
  'canceled',
  'refunded',
]);

const UNPAID_STATUSES = new Set([
  'pending',
  'partial',
  'unpaid',
  'overdue',
]);

export function isPaymentWalkIn(row) {
  if (!row) return false;
  if (row.isWalkIn) return true;
  if (isWalkInMemberName(row.member)) return true;
  if (isWalkInGymMemberId(row.gymId)) return true;
  return false;
}

/** Total due on a payment / sale row (subscriptions include fees; sales use package price). */
export function paymentTotalDue(row) {
  if (!row) return 0;
  // Walk-in sales: only package price counts — ignore leftover registration / PT / classes
  if (isPaymentWalkIn(row)) {
    return Math.max(0, Number(row.packagePrice ?? row.packageFee) || 0);
  }
  const packageFee = Number(row.packagePrice ?? row.packageFee) || 0;
  const registrationFee = Number(row.registrationFee) || 0;
  const discount = Number(row.discountAmount) || 0;
  const pt = Number(row.personalTrainingFee || row.personalTraining) || 0;
  const classes = Number(row.classesFee) || 0;
  const feeTotal = Math.max(0, packageFee - discount + registrationFee + pt + classes);
  // Any fee column (including package = 0 with registration / PT / classes) defines the due total
  if (packageFee > 0 || registrationFee > 0 || pt > 0 || classes > 0 || discount > 0) {
    return feeTotal;
  }
  const alt = Number(row.totalAmount ?? row.finalAmount) || 0;
  if (alt > 0) return alt;
  // Legacy rows with no fee columns: treat collected amount as the known total (fully settled)
  return saleRowPaid(row);
}

/** Total sale amount for ancillary / renewal sales. */
export function saleRowTotal(row) {
  return paymentTotalDue(row);
}

/** Amount paid on a sale row — prefer DB `amount`, fall back to payment splits. */
export function saleRowPaid(row) {
  if (!row) return 0;
  const fromSplits = sumPaymentSplits(parsePaymentSplitsFromRow(row)) || 0;
  let fromAmount = null;
  if (row.amount != null && row.amount !== '') {
    const n = Number(row.amount);
    if (Number.isFinite(n)) fromAmount = Math.max(0, n);
  }
  // Prefer positive amount; if amount is 0/missing but splits were saved, use splits
  if (fromAmount != null && fromAmount > 0) return fromAmount;
  if (fromSplits > 0) return fromSplits;
  if (fromAmount != null) return fromAmount;
  return 0;
}

/** True for installment / remaining-clear payment rows (not a new sale). */
export function isRemainingClearPayment(row) {
  const source = String(row?.source || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  return (
    source === 'remaining_clear'
    || source === 'remaining'
    || source === 'balance_clear'
    || source === 'balance_payment'
    || source === 'pay_balance'
  );
}

/**
 * Later balance installments appended to the original sale note by applyPaymentBalance.
 * Pattern: "Balance payment on YYYY-MM-DD · 2500" or "Balance payment on YYYY-MM-DD: note · 2500"
 */
export function remainingInstallmentsFromNote(row) {
  const note = String(row?.paymentNote || row?.payment_note || '');
  const re = /Balance payment on (\d{4}-\d{2}-\d{2})(?::[^·]*)?\s*·\s*([\d.]+)/gi;
  const items = [];
  let match;
  while ((match = re.exec(note))) {
    items.push({
      day: match[1],
      amount: Number(match[2]) || 0,
    });
  }
  return items;
}

/**
 * Cash taken on the original sale day only (excludes later remaining-clear amounts
 * that were merged into `amount` but also stored as separate remaining_clear rows).
 */
export function saleRowOriginalPaid(row) {
  const collected = saleRowPaid(row);
  if (!row || isRemainingClearPayment(row)) return collected;
  const later = remainingInstallmentsFromNote(row)
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  return Math.max(0, Math.round((collected - later) * 100) / 100);
}

/** Customer ledger Sale (credit): package/sale total; remaining-clear rows are not sales. */
export function saleRowLedgerCredit(row) {
  if (!row || isRemainingClearPayment(row)) return 0;
  return saleRowTotal(row);
}

/** Customer ledger Payment (debit): original cash, or remaining installment amount. */
export function saleRowLedgerDebit(row) {
  if (!row) return 0;
  if (isRemainingClearPayment(row)) return saleRowPaid(row);
  return saleRowOriginalPaid(row);
}

/** Remaining balance on a sale row — only when total due exceeds amount paid. */
export function saleRowLeft(row) {
  if (!row) return 0;
  const total = saleRowTotal(row);
  const paid = saleRowPaid(row);
  if (!(total > 0)) return 0;
  const computed = Math.max(0, Math.round((total - paid) * 100) / 100);
  return computed > 0 ? computed : 0;
}

/**
 * True only when a row should appear on Remaining Payments / Report.
 * Walk-ins require an explicit unpaid package balance (no phantom fees / settled status).
 */
export function saleRowHasRemainingBalance(row) {
  if (!row) return false;
  const status = String(row.status || '').toLowerCase().trim();
  if (SETTLED_STATUSES.has(status)) return false;

  const paid = saleRowPaid(row);
  const total = saleRowTotal(row);
  const left = saleRowLeft(row);

  if (isPaymentWalkIn(row)) {
    // Walk-in: must have package price > paid, and unpaid status (or blank)
    if (!(total > 0) || !(paid < total) || !(left > 0)) return false;
    if (status && !UNPAID_STATUSES.has(status)) return false;
    return true;
  }

  return left > 0 && total > 0 && paid < total;
}

/** Map membership list row to payment shape for balance pay / receipts. */
export function membershipToPaymentRow(membership) {
  if (!membership?.paymentDbId) return null;

  return {
    rawId: membership.paymentDbId,
    id: membership.paymentId || membership.paymentRef || '—',
    invoiceId: membership.invoiceId,
    member: membership.member || membership.memberName,
    gymId: membership.gymId,
    phone: membership.phone || membership.mobile || '',
    package: membership.package,
    packagePrice: Number(membership.packageFee) || 0,
    registrationFee: Number(membership.registrationFee) || 0,
    discountAmount: Number(membership.discountAmount) || 0,
    personalTrainingFee: Number(membership.personalTrainingFee) || 0,
    classesFee: Number(membership.classesFee) || 0,
    amount: Number(membership.totalPaid) || 0,
    paymentSplits: membership.paymentSplits || [],
    pendingAmount: Number(membership.pendingAmount) || 0,
    date: receiptDate(membership.paymentDate || membership.startDate || membership.start),
    status: membership.paymentStatus || (membership.pendingAmount > 0 ? 'pending' : 'completed'),
    memberId: membership.memberId,
    startDate: receiptDate(membership.startDate || membership.start),
    endDate: receiptDate(membership.endDate || membership.expiry),
    source: membership.paymentSource || 'subscription',
    paymentNote: membership.paymentNote || '',
    duration: membership.duration,
    complimentary: Boolean(membership.complimentary),
  };
}

/** Payment/receipt row from membership — uses linked payment when present, else membership fee fields. */
export function membershipRowForReceipt(membership) {
  const linked = membershipToPaymentRow(membership);
  if (linked) return linked;
  if (!membership) return null;
  return {
    id: membership.paymentId || membership.paymentRef || '—',
    invoiceId: membership.invoiceId,
    member: membership.member || membership.memberName,
    gymId: membership.gymId,
    phone: membership.phone || membership.mobile || '',
    package: membership.package,
    packagePrice: Number(membership.packageFee) || 0,
    registrationFee: Number(membership.registrationFee) || 0,
    discountAmount: Number(membership.discountAmount) || 0,
    personalTrainingFee: Number(membership.personalTrainingFee) || 0,
    classesFee: Number(membership.classesFee) || 0,
    amount: Number(membership.totalPaid) || 0,
    paymentSplits: membership.paymentSplits || [],
    pendingAmount: Number(membership.pendingAmount) || 0,
    date: receiptDate(membership.paymentDate || membership.startDate || membership.start),
    status: membership.complimentary
      ? 'complimentary'
      : (membership.paymentStatus || (membership.pendingAmount > 0 ? 'pending' : 'completed')),
    startDate: receiptDate(membership.startDate || membership.start),
    endDate: receiptDate(membership.endDate || membership.expiry),
    source: membership.paymentSource || 'subscription',
    paymentNote: membership.paymentNote || '',
    duration: membership.duration,
    complimentary: Boolean(membership.complimentary),
  };
}

export function resolveSalePayment(totalAmount, paymentSplits) {
  return resolveSplitPayment(totalAmount, paymentSplits);
}
